import { useQuery } from "@tanstack/react-query";
import { IntlProvider } from "react-intl";

import { LANGUAGE_ID } from "../../lang/lang.constants";

import { CfLoader } from "../common/CfLoader/CfLoader";

const loadTranslations = async (
  langId: LANGUAGE_ID,
  signal?: AbortSignal,
): Promise<Record<string, string>> => {
  if (signal?.aborted) {
    throw new Error("Request was aborted");
  }

  try {
    const module = await import(`../../lang/${langId}.json`);
    return module.default || module;
  } catch (error) {
    throw new Error(`Failed to load translations for ${langId}`);
  }
};

interface UseTranslationsResult {
  translations: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
}

export function useTranslations(langId: LANGUAGE_ID): UseTranslationsResult {
  const {
    data: translations = {},
    error,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: ["translations", langId],
    queryFn: ({ signal }) => loadTranslations(langId, signal),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    translations,
    isLoading,
    error: error as Error | null,
    isSuccess,
  };
}

interface DynamicIntlProviderProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  locale: LANGUAGE_ID;
  onError?: (error: string) => void;
}

export const DynamicIntlProvider: React.FC<DynamicIntlProviderProps> = ({
  children,
  fallback = <CfLoader />,
  locale,
  onError,
}) => {
  const { error, isLoading, isSuccess, translations } = useTranslations(locale);

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (error) {
    const errorMessage = `Failed to load translations for ${locale}: ${error.message}`;
    // eslint-disable-next-line no-console
    console.error(errorMessage);

    if (onError) {
      onError(errorMessage);
    }

    if (locale !== "en-US") {
      // eslint-disable-next-line no-console
      console.warn(`Falling back to English translations for ${locale}`);
      return (
        <DynamicIntlProvider
          fallback={fallback}
          locale="en-US"
          onError={onError}
        >
          {children}
        </DynamicIntlProvider>
      );
    }

    return <>{fallback}</>;
  }

  if (isSuccess) {
    return (
      <IntlProvider
        locale={locale}
        messages={translations}
        onError={(err) => {
          // eslint-disable-next-line no-console
          console.warn("React Intl error:", err);
        }}
      >
        {children}
      </IntlProvider>
    );
  }

  return <>{fallback}</>;
};

// eslint-disable-next-line import/no-default-export
export default DynamicIntlProvider;
